/**
 * 英語ページ(/en/)のナビ・共通 UI 文言(docs/_prompts/20260917_en_site_spec.md §2-4)。
 * 構造は nav.ts と同一(href は /en 前置)。自然な英国英語・PC 1 行に収まる短さ。
 * 用語集(固定訳): The Salon / The Studio / Apprentice Butler / Online Shop / Enquiries & Reservations。
 * 「派遣」に当たる dispatch / staffing は使わない。
 */
import type { FooterNavSection, GlobalNavItem, NavLink, ServiceKey, ServiceNavConfig } from './nav'
import { footerNav as footerNavJa, globalContact as globalContactJa, globalNav as globalNavJa, serviceNav as serviceNavJa } from './nav'
import type { Lang } from '@/lib/lang'

export const globalNavEn: GlobalNavItem[] = [
  { label: 'First Visit', href: '/en/first-time/', children: [
    { label: "About Queen's Butler", href: '/en/first-time/about/' },
    { label: 'The Manor', href: '/en/first-time/rooms/' },
    { label: 'How It Works', href: '/en/first-time/flow/' },
    { label: 'Access', href: '/en/first-time/access/' },
  ] },
  { label: 'The Salon', href: '/en/salon/', children: [
    { label: 'About The Salon', href: '/en/salon/about/' },
    { label: 'Your Time Here', href: '/en/salon/experience/' },
    { label: 'Buddy & Membership', href: '/en/salon/buddy/' },
    { label: 'Prices', href: '/en/salon/price/' },
  ] },
  { label: 'The Studio', href: '/en/studio/', children: [
    { label: 'About The Studio', href: '/en/studio/about/' },
    { label: 'Purposes', href: '/en/studio/uses/' },
    { label: 'Space & Facilities', href: '/en/studio/space/' },
    { label: 'Prices', href: '/en/studio/price/' },
  ] },
  { label: 'Apprentice Butler', href: '/en/apprentice/', children: [
    { label: 'About the Experience', href: '/en/apprentice/about/' },
    { label: 'Programme', href: '/en/apprentice/program/' },
    { label: 'Wardrobe', href: '/en/apprentice/wardrobe/' },
    { label: 'Prices', href: '/en/apprentice/price/' },
  ] },
  { label: 'Online Shop', href: '/en/shop/', children: [
    { label: 'New & Recommended', href: '/en/shop/#new-and-recommended' },
    { label: 'By Category', href: '/en/shop/category/' },
    { label: 'Design Collection', href: '/en/shop/collection/' },
    { label: 'Shopping Guide', href: '/en/shop/guide/' },
  ] },
]

export const globalContactEn: NavLink = { label: 'Enquiries & Reservations', href: '/en/contact/' }

export const serviceNavEn: Record<ServiceKey, ServiceNavConfig> = {
  salon: { key: 'salon', prefix: '/salon/', lockup: 'THE SALON', subLockup: "Queen's Butler", label: 'The Salon', links: [
    { label: 'About', href: '/en/salon/about/' }, { label: 'Your Time Here', href: '/en/salon/experience/' },
    { label: 'Buddy & Membership', href: '/en/salon/buddy/' }, { label: 'Prices', href: '/en/salon/price/' },
    { label: 'FAQ', href: '/en/salon/faq/' }, { label: 'Enquiries & Reservations', href: '/en/salon/contact/' },
  ] },
  studio: { key: 'studio', prefix: '/studio/', lockup: 'THE STUDIO', subLockup: "Queen's Butler", label: 'The Studio', links: [
    { label: 'About', href: '/en/studio/about/' }, { label: 'Purposes', href: '/en/studio/uses/' },
    { label: 'Space & Facilities', href: '/en/studio/space/' }, { label: 'Prices', href: '/en/studio/price/' },
    { label: 'FAQ', href: '/en/studio/faq/' }, { label: 'Enquiries & Reservations', href: '/en/studio/contact/' },
  ] },
  apprentice: { key: 'apprentice', prefix: '/apprentice/', lockup: 'THE FOOTMAN', subLockup: "Queen's Butler", label: 'Apprentice Butler', links: [
    { label: 'About', href: '/en/apprentice/about/' }, { label: 'Programme', href: '/en/apprentice/program/' },
    { label: 'Wardrobe', href: '/en/apprentice/wardrobe/' }, { label: 'Prices', href: '/en/apprentice/price/' },
    { label: 'FAQ', href: '/en/apprentice/faq/' }, { label: 'Enquiries & Reservations', href: '/en/apprentice/contact/' },
  ] },
  shop: { key: 'shop', prefix: '/shop/', lockup: 'THE SHOP', subLockup: "Queen's Butler", label: 'Online Shop', links: [
    { label: 'About', href: '/en/shop/about/' }, { label: 'All Items', href: '/en/shop/items/' },
    { label: 'Design Collection', href: '/en/shop/collection/' }, { label: 'Shopping Guide', href: '/en/shop/guide/' },
    { label: 'FAQ', href: '/en/shop/faq/' }, { label: 'Enquiries', href: '/en/shop/contact/' },
  ], cart: { label: 'Basket', href: '/en/shop/#cart' } },
}

export const footerNavEn: FooterNavSection[] = [
  { label: 'Overview', links: [
    { label: 'Home', href: '/en/' }, { label: 'The Salon', href: '/en/salon/' },
    { label: 'The Studio', href: '/en/studio/' }, { label: 'Apprentice Butler', href: '/en/apprentice/' },
    { label: 'Online Shop', href: '/en/shop/' },
  ] },
  { label: 'About Butlers', links: [
    { label: 'About Butlers', href: '/en/butler/' }, { label: 'Attire', href: '/en/butler/attire/' },
    { label: 'Manners', href: '/en/butler/manners/' }, { label: 'History', href: '/en/butler/history/' },
    { label: 'The Manor & Its Furnishings', href: '/en/butler/mansion/' }, { label: 'The Work of a Butler', href: '/en/butler/work/' },
  ] },
  { label: 'Butler Column', links: [{ label: 'Butler Column', href: '/en/column/' }] },
  { label: 'FAQ by Service', links: [
    { label: 'The Salon', href: '/en/salon/faq/' }, { label: 'The Studio', href: '/en/studio/faq/' },
    { label: 'Apprentice Butler', href: '/en/apprentice/faq/' }, { label: 'Online Shop', href: '/en/shop/faq/' },
  ] },
  { label: 'Cancellation Policy', links: [
    { label: 'The Salon', href: '/en/salon/price/cancel-policy/' }, { label: 'The Studio', href: '/en/studio/price/cancel-policy/' },
    { label: 'Apprentice Butler', href: '/en/apprentice/price/cancel-policy/' }, { label: 'Online Shop', href: '/en/shop/guide/cancel-policy/' },
  ] },
  { label: 'Careers', links: [{ label: 'Careers', href: '/en/recruit/' }] },
  { label: 'Company', links: [
    { label: 'Company', href: '/en/company/' }, { label: 'Terms of Use', href: '/en/terms/' },
    { label: 'Privacy Policy', href: '/en/privacy/' }, { label: 'Legal Notice', href: '/en/tokushoho/' },
    { label: 'Enquiries & Reservations', href: '/en/contact/' },
  ] },
  { label: 'Related Sites', links: [
    { label: 'Related Sites', href: '/en/related/' }, { label: 'Japan Butler Association', href: '/en/related/association/' },
    { label: 'THE BUTLER', href: '/en/related/thebutler/' }, { label: 'Japan Butler Academy', href: '/en/related/academy/' },
    { label: 'Butler Bell', href: '/en/related/bell/' },
  ] },
]

/** フッター(Footer.tsx の services / lower / legal と同形) */
export const footerEn = {
  legal: [
    { label: 'Home', href: '/en/' }, { label: 'Terms of Use', href: '/en/terms/' },
    { label: 'Privacy Policy', href: '/en/privacy/' }, { label: 'Legal Notice', href: '/en/tokushoho/' },
    { label: 'Enquiries', href: '/en/contact/' },
  ] as NavLink[],
  services: [
    { tone: 'salon', en: 'The Salon', ja: 'Salon', links: [
      ['About The Salon', '/en/salon/about/'], ['Your Time Here', '/en/salon/experience/'], ['Buddy & Membership', '/en/salon/buddy/'], ['Prices', '/en/salon/price/'], ['FAQ', '/en/salon/faq/'], ['Cancellation Policy', '/en/salon/price/cancel-policy/'], ['Enquiries & Reservations', '/en/salon/contact/'],
    ] },
    { tone: 'studio', en: 'The Studio', ja: 'Studio & Rental Space', links: [
      ['About The Studio', '/en/studio/about/'], ['Purposes', '/en/studio/uses/'], ['Space & Facilities', '/en/studio/space/'], ['Photography Plans', '/en/studio/plans/'], ['Scenes', '/en/studio/scenes/'], ['Usage Guide', '/en/studio/guide/'], ['Prices', '/en/studio/price/'], ['FAQ', '/en/studio/faq/'], ['Cancellation Policy', '/en/studio/price/cancel-policy/'], ['Enquiries & Reservations', '/en/studio/contact/'],
    ] },
    { tone: 'footman', en: 'The Footman', ja: 'Apprentice Butler', links: [
      ['About the Experience', '/en/apprentice/about/'], ['Programme', '/en/apprentice/program/'], ['Wardrobe', '/en/apprentice/wardrobe/'], ['Prices', '/en/apprentice/price/'], ['FAQ', '/en/apprentice/faq/'], ['Cancellation Policy', '/en/apprentice/price/cancel-policy/'], ['Enquiries & Reservations', '/en/apprentice/contact/'],
    ] },
    { tone: 'shop', en: 'The Shop', ja: 'Online Shop', links: [
      ['About the Shop', '/en/shop/about/'], ['All Items', '/en/shop/items/'], ['By Category', '/en/shop/category/'], ['Design Collection', '/en/shop/collection/'], ['Shopping Guide', '/en/shop/guide/'], ['FAQ', '/en/shop/faq/'], ['Cancellation Policy', '/en/shop/guide/cancel-policy/'], ['Enquiries', '/en/shop/contact/'],
    ] },
  ] as const,
  lower: [
    { label: 'First Visit', links: [["About Queen's Butler", '/en/first-time/about/'], ['The Manor', '/en/first-time/rooms/'], ['Origins of the Furnishings', '/en/first-time/collection/'], ['Silver & Porcelain', '/en/first-time/tableware/'], ['How It Works', '/en/first-time/flow/'], ['Payment', '/en/first-time/flow/payment/'], ['Access', '/en/first-time/access/']] },
    { label: 'About Butlers', links: [['What Is a Butler', '/en/butler/'], ['Attire', '/en/butler/attire/'], ['Manners', '/en/butler/manners/'], ['History', '/en/butler/history/'], ['The Manor & Its Furnishings', '/en/butler/mansion/'], ['The Work of a Butler', '/en/butler/work/'], ['Butler Column', '/en/column/']] },
    { label: 'Company', links: [['Company', '/en/company/'], ['Careers', '/en/recruit/'], ['Application Form', '/en/recruit/entry/'], ['Terms of Use', '/en/terms/'], ['Privacy Policy', '/en/privacy/'], ['Legal Notice', '/en/tokushoho/'], ['Enquiries & Reservations', '/en/contact/']] },
    { label: 'Related Sites', links: [['All Related Sites', '/en/related/'], ['Japan Butler Association', '/en/related/association/'], ['THE BUTLER', '/en/related/thebutler/'], ['Japan Butler Academy', '/en/related/academy/'], ['Butler Bell', '/en/related/bell/']] },
  ] as const,
  address: '2-7-4 Aomi, Koto-ku, Tokyo · By appointment only · A butler is always at your side',
  gl: [{ label: 'Home', href: '/en/' }, { label: 'First Visit', href: '/en/first-time/' }, { label: 'Careers', href: '/en/recruit/' }, { label: 'Enquiries & Reservations', href: '/en/contact/' }] as NavLink[],
  copyright: '© Japan Butler Association',
  copyrightLong: "© Japan Butler Association — Queen's Butler",
  tagline: 'The manor where a butler is always at your side — Odaiba, Tokyo',
}

/** ヘッダー・共通 UI の文言(JA/EN)。構造・CSS は共通で文字列だけ切り替える */
export const ui = {
  ja: {
    home: '/', backToTop: '全体TOPに戻る', company: '運営会社', recruit: '採用募集', recruitEn: 'Recruit',
    topSuffix: ' トップ', listSuffix: ' 一覧', cart: 'カート（0）', contact: 'お問い合わせ',
    langLabel: '言語', brandTop: "Queen's Butler トップ", navGlobal: '全体ナビゲーション', navMobile: '全体モバイルナビゲーション',
    navService: (label: string) => `${label}ナビゲーション`, navServiceMobile: (label: string) => `${label}モバイルナビゲーション`,
    serviceTop: (label: string) => `${label} トップ`, openNav: 'ナビを開く', closeNav: 'ナビを閉じる',
    subOpen: (label: string) => `${label}のサブメニューを開く`, subClose: (label: string) => `${label}のサブメニューを閉じる`,
    breadcrumbHome: 'トップ', breadcrumb: 'パンくずリスト', related: 'あわせてご覧ください', relatedEyebrow: 'Related',
    definitionOf: (name: string) => `${name}とは`, guideOf: (name: string) => `${name}のご案内`, interim: '暫定ページ',
    notFoundTitle: "ページが見つかりません｜Queen's Butler", notFoundDesc: 'お探しのページは見つかりませんでした。',
    notFoundH1: 'ページが見つかりません', notFoundBody: 'URLをご確認いただくか、トップから目的のページをお探しください。', notFoundBack: 'トップへ戻る',
    shopPreparingEn: 'Coming Soon', shopPreparingH: 'オンラインショップは、只今準備中でございます',
    shopPreparingP: '商品のご案内が整い次第、こちらでご覧いただけます。ご希望の品やご質問は、お問い合わせフォームからお知らせくださいませ。',
    shopPreparingLink: 'お問い合わせフォームへ', shopContact: '/shop/contact/',
  },
  en: {
    home: '/en/', backToTop: 'Back to top', company: 'Company', recruit: 'Careers', recruitEn: 'Careers',
    topSuffix: ' — Top', listSuffix: ' — All', cart: 'Basket (0)', contact: 'Enquiries & Reservations',
    langLabel: 'Language', brandTop: "Queen's Butler home", navGlobal: 'Main navigation', navMobile: 'Main mobile navigation',
    navService: (label: string) => `${label} navigation`, navServiceMobile: (label: string) => `${label} mobile navigation`,
    serviceTop: (label: string) => `${label} home`, openNav: 'Open menu', closeNav: 'Close menu',
    subOpen: (label: string) => `Open ${label} submenu`, subClose: (label: string) => `Close ${label} submenu`,
    breadcrumbHome: 'Home', breadcrumb: 'Breadcrumb', related: 'You may also wish to read', relatedEyebrow: 'Related',
    definitionOf: (name: string) => `About ${name}`, guideOf: (name: string) => `About ${name}`, interim: 'Provisional page',
    notFoundTitle: "Page Not Found | Queen's Butler", notFoundDesc: 'The page you are looking for could not be found.',
    notFoundH1: 'Page not found', notFoundBody: 'Please check the address, or return to the home page to find what you are looking for.', notFoundBack: 'Back to home',
    shopPreparingEn: 'Coming Soon', shopPreparingH: 'Our online shop is currently being prepared',
    shopPreparingP: 'Our items will appear here as soon as they are ready. If there is anything you would like, or any question we can answer, please use the enquiry form.',
    shopPreparingLink: 'Go to the enquiry form', shopContact: '/en/shop/contact/',
  },
} as const

/** 言語別のナビ一式 */
export function navFor(lang: Lang) {
  return lang === 'en'
    ? { globalNav: globalNavEn, globalContact: globalContactEn, serviceNav: serviceNavEn, footerNav: footerNavEn, ui: ui.en }
    : { globalNav: globalNavJa, globalContact: globalContactJa, serviceNav: serviceNavJa, footerNav: footerNavJa, ui: ui.ja }
}
